import React, { useState } from 'react'
import SiteUsers from './component'
import { inputModeType } from 'lib/util'
import { Site, SiteUser } from 'types'

type BulkAddUsersResponse = {
  newSiteUsers?: SiteUser[]
  failedSiteUsers?: SiteUser[]
  invalidSiteUsers?: SiteUser[]
  sendEmail?: boolean
}

type RemindSiteUsersFilters = {
  pendingUser?: boolean
  roles?: string[]
}

interface SiteUsersContainerProps {
  addBulkUsersResponse?: BulkAddUsersResponse
  createSiteUser: (
    siteUser: Partial<SiteUser>,
    sendNotification: boolean,
    selectedSite?: Site,
  ) => Promise<void>
  createSiteUsers: (
    siteId: number,
    users: Partial<SiteUser>[],
    sendNotification: boolean,
  ) => Promise<void>
  error?: Error
  handleCancelUserAccess: (user: SiteUser) => void
  handleGrantNewRole: (user: SiteUser, newRole: string) => void
  isBulkOperationCompleted?: boolean
  remindSiteUser: (siteUser: SiteUser) => void
  remindSiteUsers: (
    siteId: number,
    emailFilters?: RemindSiteUsersFilters,
  ) => void
  resetBulkAddUsers: () => void
  selectedSite?: Site
  siteUsers?: SiteUser[]
}

const Container: React.FC<SiteUsersContainerProps> = ({
  addBulkUsersResponse,
  isBulkOperationCompleted,
  siteUsers,
  createSiteUser,
  createSiteUsers,
  remindSiteUser,
  remindSiteUsers,
  resetBulkAddUsers,
  error,
  selectedSite,
  handleCancelUserAccess,
  handleGrantNewRole,
}) => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)

  const handleCloseAddUsersModal = () => {
    resetBulkAddUsers()
    setIsAddUserModalOpen(false)
  }

  const handleSubmitAddUsers = async (
    emails: string[],
    role: string,
    sendNotification: boolean,
    inputMode: string,
  ) => {
    const siteId = selectedSite?.id
    if (!siteId) return

    const addSiteUsers = emails.map(email => ({
      inviteEmail: email.trim(),
      isActive: true,
      roles: [role],
      siteId,
    }))

    if (inputMode === inputModeType.bulk) {
      await createSiteUsers(siteId, addSiteUsers, sendNotification)
    } else {
      await createSiteUser(addSiteUsers[0], sendNotification, selectedSite)
      resetBulkAddUsers()
      setIsAddUserModalOpen(false)
    }
  }

  return (
    <SiteUsers
      key={selectedSite?.id}
      addBulkUsersResponse={addBulkUsersResponse}
      handleCancelUserAccess={handleCancelUserAccess}
      handleGrantNewRole={handleGrantNewRole}
      isBulkOperationCompleted={isBulkOperationCompleted}
      siteUsers={siteUsers}
      site={selectedSite}
      handleCloseAddUsersModal={handleCloseAddUsersModal}
      handleOpenAddUsersModal={() => setIsAddUserModalOpen(true)}
      handleSubmitAddUsers={handleSubmitAddUsers}
      isAddUserModalOpen={isAddUserModalOpen}
      remindSiteUser={remindSiteUser}
      remindSiteUsers={remindSiteUsers}
      error={error}
      selectedSite={selectedSite}
    />
  )
}

export default Container
